// Opening hours on the branded chat page (/c/:slug).
//
// The API sends 7 entries {day 0=Sunday..6, open, opens 'HH:MM', closes
// 'HH:MM'} plus the zone they are in (Asia/Damascus for now). "Open now" is
// worked out in THAT zone, not the visitor's — someone abroad looking at a
// Damascus shop must see the shop's clock. A closing time earlier than the
// opening time means "after midnight", so a 18:00–02:00 Thursday is still
// open at 01:00 on Friday.

// Saturday first: the working week in the region the product serves.
export const WEEK_ORDER = [6, 0, 1, 2, 3, 4, 5];

const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function minutes(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

// Day of week and minutes since midnight in `timeZone`. An unknown zone falls
// back to the visitor's own clock rather than breaking the page.
export function clockIn(timeZone, date = new Date()) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
  } catch {
    return {
      day: date.getDay(),
      minutes: date.getHours() * 60 + date.getMinutes(),
    };
  }
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    day: WEEKDAYS[get('weekday')],
    minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')),
  };
}

export function isOpenAt(hours, { day, minutes: now }) {
  if (!Array.isArray(hours) || !hours.length) return false;
  const entry = (d) => hours.find((h) => h.day === d && h.open);
  const today = entry(day);
  if (today) {
    const o = minutes(today.opens);
    const c = minutes(today.closes);
    if (o !== null && c !== null) {
      if (c > o ? now >= o && now < c : now >= o) return true;
    }
  }
  // Yesterday's late shift running past midnight.
  const yesterday = entry((day + 6) % 7);
  if (yesterday) {
    const o = minutes(yesterday.opens);
    const c = minutes(yesterday.closes);
    if (o !== null && c !== null && c < o && now < c) return true;
  }
  return false;
}

// 'HH:MM' → '9:00 AM' / '9:00 ص', with Latin digits in both languages.
export function formatTime(hhmm, lang) {
  const m = minutes(hhmm);
  if (m === null) return '';
  const d = new Date(Date.UTC(2000, 0, 1, Math.floor(m / 60), m % 60));
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-u-nu-latn' : 'en', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(d);
}

export function dayName(day, lang) {
  // 2000-01-02 was a Sunday.
  const d = new Date(Date.UTC(2000, 0, 2 + day));
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : 'en', {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(d);
}
